/** Accelerometer calibration (#16) and the g-g envelope.
 *
 * GT7 broadcasts `sway`/`heave`/`surge` (packet B) with no documented unit and
 * no documented sign convention, and the simulator source cannot prove what a
 * real console sends — which is exactly why #16 said "validate before building
 * UI". So the app validates them against physics it already records, per lap:
 *
 *   lateral       a = v * omega      omega = signed heading rate from the path
 *   longitudinal  a = dv/dt          from the speed trace
 *
 * Both references are in m/s^2 and both are signed, so a least-squares slope
 * through the origin recovers the broadcast channel's UNIT (slope ~1 means it
 * is m/s^2, ~0.102 means it is already g) and its SIGN at the same time. What
 * comes back is a multiplier to g, so a g-g diagram reads correctly whichever
 * convention the console turns out to use — and says so when the fit is too
 * weak to trust.
 */

import { column } from './columns';
import { wrapAngle } from './corners';
import { bisectLeft } from './interp';
import { pyFloat, roundTo } from './round';
import type { AccelAxis, AccelCalibration, GgExtremes, Samples } from './types';

export const GRAVITY = 9.80665;

/** A lap has thousands of ticks; a fit resting on a handful of them is noise. */
export const ACC_MIN_SAMPLES = 150;
/** Share of the channel's magnitude the scaled reference has to explain.
 *  This is R² about ZERO, not about the mean: the model is forced through
 *  the origin (see fitThroughOrigin), and a mean-centred correlation
 *  collapses on exactly the cleanest case there is — a long constant-rate
 *  braking zone, where both series are near-constant and Pearson r becomes
 *  numerical noise. */
export const ACC_MIN_R2 = 0.75;
/** Only fit where the reference itself is large enough to have a meaningful
 *  ratio: near zero the quotient is dominated by jitter in both series. */
export const ACC_MIN_REF = 1.5; // m/s^2
export const ACC_MIN_SPEED = 8; // m/s — below this, heading from position is noise
/** A slope this small would mean the channel is ~flat against the
 *  reference; inverting it produces an absurd scale rather than a
 *  calibration. */
export const ACC_MIN_SLOPE = 1e-3;

/** (slope, R² about zero) for y = slope * x, forced through the origin.
 *
 * The intercept is fixed at zero on purpose: zero reference acceleration
 * must mean zero broadcast acceleration, and a free intercept would happily
 * absorb a systematic offset that is the very thing worth seeing. The
 * goodness measure is taken about zero for the same reason — how much of the
 * channel's own magnitude the scaled reference accounts for. It is 1 for a
 * perfect fit and goes negative for a channel the reference does not
 * describe at all.
 */
export function fitThroughOrigin(xs: readonly number[], ys: readonly number[]): [number, number] {
  if (xs.length < 2) return [0, 0];
  let sxx = 0;
  for (const x of xs) sxx += x * x;
  let syy = 0;
  for (const y of ys) syy += y * y;
  if (sxx <= 0 || syy <= 0) return [0, 0];
  let sxy = 0;
  for (let i = 0; i < xs.length; i++) sxy += xs[i]! * ys[i]!;
  const slope = sxy / sxx;
  let sse = 0;
  for (let i = 0; i < xs.length; i++) {
    const r = ys[i]! - slope * xs[i]!;
    sse += r * r;
  }
  return [slope, 1 - sse / syy];
}

/** Half-window for the path heading, in METRES of track rather than ticks.
 *  Positions are stored rounded to a centimetre, so heading resolution is
 *  0.01 / chord: over the ~1 m a tick covers at speed that is 0.01 rad, which
 *  swamps the 0.002 rad/tick a real corner turns, and differencing
 *  consecutive ticks yields a staircase of quantisation noise rather than a
 *  yaw rate. Over 8 m the same rounding is 0.001 rad, two orders below what
 *  a 46 m radius turns across that distance. Distance-based because the tick
 *  spacing itself varies with speed by a factor of five. */
export const HEADING_SPAN_M = 8;

/** Signed yaw rate (rad/s) from the driven path, per tick.
 *
 * The stored `yaw_rate` column is an ABSOLUTE value, so it cannot settle
 * which way `sway` counts positive. Positive here is the same convention
 * corner detection uses: a positive heading delta in raw x/z, which the map
 * (z inverted, as GT7 draws it) renders as a right-hander.
 *
 * The chord-heading-difference scheme is the one `detectCorners` uses, for
 * the same reason: it measures the turn over a fixed length of road instead
 * of a fixed number of samples.
 */
export function headingRate(samples: Samples): number[] {
  const t = column(samples, 't') ?? [];
  const xs = column(samples, 'pos_x') ?? [];
  const zs = column(samples, 'pos_z') ?? [];
  const dist = column(samples, 'dist') ?? [];
  const n = Math.min(t.length, xs.length, zs.length, dist.length);
  const out = new Array<number>(n).fill(0);
  if (n < 5) return out;
  for (let i = 0; i < n; i++) {
    const lo = bisectLeft(dist, dist[i]! - HEADING_SPAN_M, 0, i);
    let hi = bisectLeft(dist, dist[i]! + HEADING_SPAN_M, i, n);
    hi = Math.min(hi, n - 1);
    if (hi - i < 1 || i - lo < 1) continue;
    const before = Math.atan2(zs[i]! - zs[lo]!, xs[i]! - xs[lo]!);
    const after = Math.atan2(zs[hi]! - zs[i]!, xs[hi]! - xs[i]!);
    // The two chords represent the heading at their own midpoints in time.
    const dt = (t[hi]! - t[lo]!) / 2;
    if (dt > 0) out[i] = wrapAngle(after - before) / dt;
  }
  return out;
}

/** Calibrate one accelerometer axis against a physical reference. */
function axis(raw: readonly number[], reference: readonly number[], mask: readonly boolean[]): AccelAxis {
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < mask.length; i++) {
    if (mask[i]) {
      xs.push(reference[i]!);
      ys.push(raw[i]!);
    }
  }
  const [slope, r2] = fitThroughOrigin(xs, ys);
  const ok = xs.length >= ACC_MIN_SAMPLES && r2 >= ACC_MIN_R2 && Math.abs(slope) >= ACC_MIN_SLOPE;
  return {
    slope: roundTo(slope, 5), // broadcast units per m/s^2
    r2: roundTo(r2, 4),
    samples: xs.length,
    fitted: ok,
    // What a consumer multiplies the raw channel by to get g. Carries the
    // sign, so a channel that counts the other way comes out upright.
    g_per_unit: ok ? roundTo(1 / (slope * GRAVITY), 6) : roundTo(1 / GRAVITY, 6),
  };
}

/** Unit + sign calibration for a lap's broadcast accelerometer channels.
 *
 * Returns `available: false` for a recording made on packet A (no
 * accelerometer at all). When a fit is too weak the axis falls back to
 * assuming m/s^2 and reports `fitted: false`, so the UI can show the
 * diagram and still say the calibration is unproven.
 */
export function accelCalibration(samples: Samples): AccelCalibration {
  const latRaw = column(samples, 'acc_lat') ?? [];
  const longRaw = column(samples, 'acc_long') ?? [];
  const t = column(samples, 't') ?? [];
  const speed = column(samples, 'speed') ?? [];
  const heading = headingRate(samples);
  const n = Math.min(t.length, speed.length, latRaw.length, longRaw.length, heading.length);
  if (n < ACC_MIN_SAMPLES) return { available: false };

  const v = new Array<number>(n);
  for (let i = 0; i < n; i++) v[i] = speed[i]! / 3.6; // km/h -> m/s
  const latRef = new Array<number>(n);
  for (let i = 0; i < n; i++) latRef[i] = v[i]! * heading[i]!;
  const longRef = new Array<number>(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    const dt = t[i + 1]! - t[i - 1]!;
    if (dt > 0) longRef[i] = (v[i + 1]! - v[i - 1]!) / dt;
  }

  const latMask = new Array<boolean>(n);
  const longMask = new Array<boolean>(n);
  for (let i = 0; i < n; i++) {
    const moving = v[i]! >= ACC_MIN_SPEED;
    latMask[i] = moving && Math.abs(latRef[i]!) >= ACC_MIN_REF;
    longMask[i] = moving && Math.abs(longRef[i]!) >= ACC_MIN_REF;
  }
  const lat = axis(latRaw, latRef, latMask);
  const lon = axis(longRaw, longRef, longMask);
  return {
    available: true,
    lateral: lat,
    longitudinal: lon,
    // One line a human can read: what the broadcast unit appears to be.
    unit: unitGuess(lat, lon),
  };
}

function unitGuess(lat: AccelAxis, lon: AccelAxis): string {
  const slopes: number[] = [];
  for (const a of [lat, lon]) if (a.fitted) slopes.push(Math.abs(a.slope));
  if (slopes.length === 0) return 'unverified';
  let sum = 0;
  for (const s of slopes) sum += s;
  const mean = sum / slopes.length;
  if (0.7 <= mean && mean <= 1.4) return 'm/s^2';
  if (0.07 <= mean && mean <= 0.14) return 'g';
  return `1 unit = ${pyFloat(roundTo(1 / mean, 3))} m/s^2`;
}

/** Peak g actually used, in each direction — the corners of the envelope. */
export function ggExtremes(samples: Samples, calibration: AccelCalibration): GgExtremes {
  const latK = calibration.available ? calibration.lateral.g_per_unit : 1 / GRAVITY;
  const longK = calibration.available ? calibration.longitudinal.g_per_unit : 1 / GRAVITY;
  const latRaw = column(samples, 'acc_lat') ?? [];
  const longRaw = column(samples, 'acc_long') ?? [];
  let latMax = -Infinity;
  let latMin = Infinity;
  for (const raw of latRaw) {
    const v = raw * latK;
    if (v > latMax) latMax = v;
    if (v < latMin) latMin = v;
  }
  let lonMax = -Infinity;
  let lonMin = Infinity;
  for (const raw of longRaw) {
    const v = raw * longK;
    if (v > lonMax) lonMax = v;
    if (v < lonMin) lonMin = v;
  }
  if (latRaw.length === 0) latMax = latMin = 0;
  if (longRaw.length === 0) lonMax = lonMin = 0;
  // Clamped at zero: a direction the lap never went in used no g at all, and
  // an oval driven one way round would otherwise report a negative peak for
  // the side it never turned to.
  return {
    lat_right: roundTo(Math.max(0, latMax), 3),
    lat_left: roundTo(Math.max(0, -latMin), 3),
    accel: roundTo(Math.max(0, lonMax), 3),
    braking: roundTo(Math.max(0, -lonMin), 3),
  };
}
